'use client';

import { useCallback, useEffect, useState } from 'react';
import { getCartBrandsForLocale, getLocale } from '@libs/core';

import type { CoreOptions } from '@adyen/adyen-web/dist/types/core/types';
import type CardElement from '@adyen/adyen-web/dist/types/components/Card/Card';

import { waitForElement } from '../utils';
import type { useAdyenInterface, CardState } from './types';

// https://github.com/Adyen/adyen-web/blob/c6c19ac646a8a211d0416a462b2c2a2a722eff29/packages/lib/src/language/locales/en-US.json -- Possible Adyen translations
const translations = {
    'en-US': {
        'creditCard.cvcField.title': '',
        'creditCard.expiryDateField.title': '',
        'creditCard.holderName': '',
        'creditCard.numberField.title': '',
        'creditCard.numberField.placeholder': 'Card number',
        'creditCard.expiryDateField.placeholder': 'Exp MM/YY',
        'creditCard.cvcField.placeholder': 'CVV',
        'creditCard.cvcField.placeholder.3digits': 'CVV',
        'creditCard.cvcField.placeholder.4digits': 'CVV',
        'creditCard.holderName.placeholder': 'Name on card',
        'creditCard.holderName.invalid': 'Name on card is required',
        'error.va.sf-cc-num.01': 'Card number is not valid',
        'error.va.sf-cc-num.02': 'Card number is required',
        'error.va.sf-cc-num.03': 'Enter a supported card brand',
        'error.va.sf-cc-num.04': 'Card number is the wrong length',
        'error.va.sf-cc-dat.01': 'Expiry date: card too old',
        'error.va.sf-cc-dat.02': 'Expiry date: date too far in the future',
        'error.va.sf-cc-dat.04': 'Expiration is required',
        'error.va.sf-cc-dat.05': 'Expiry date is the wrong length',
        'error.va.sf-cc-cvc.01': 'CVV is required',
        'error.va.sf-cc-cvc.02': 'Security code is the wrong length',
    },
    'en-CA': {
        'creditCard.cvcField.title': '',
        'creditCard.expiryDateField.title': '',
        'creditCard.holderName': '',
        'creditCard.numberField.title': '',
        'creditCard.numberField.placeholder': 'Card number',
        'creditCard.expiryDateField.placeholder': 'Exp MM/YY',
        'creditCard.cvcField.placeholder': 'CVV',
        'creditCard.cvcField.placeholder.3digits': 'CVV',
        'creditCard.cvcField.placeholder.4digits': 'CVV',
        'creditCard.holderName.placeholder': 'Name on card',
        'creditCard.holderName.invalid': 'Name on card is required',
        'error.va.sf-cc-num.01': 'Card number is not valid',
        'error.va.sf-cc-num.02': 'Card number is required',
        'error.va.sf-cc-num.03': 'Enter a supported card brand',
        'error.va.sf-cc-num.04': 'Card number is the wrong length',
        'error.va.sf-cc-dat.01': 'Expiry date: card too old',
        'error.va.sf-cc-dat.02': 'Expiry date: date too far in the future',
        'error.va.sf-cc-dat.04': 'Expiration is required',
        'error.va.sf-cc-dat.05': 'Expiry date is the wrong length',
        'error.va.sf-cc-cvc.01': 'CVV is required',
        'error.va.sf-cc-cvc.02': 'Security code is the wrong length',
    },
};

const adyenBaseConfiguration = {
    analytics: {
        enabled: false,
    },
    clientKey: process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY,
    environment: process.env.NEXT_PUBLIC_ADYEN_ENVIRONMENT,
    locale: getLocale(),
    translations,
};

const adyenCardStyles = {
    base: {
        fontFamily: 'sans-serif',
        fontSize: '16px',
    },
    placeholder: {
        fontFamily: 'sans-serif',
        fontSize: '16px',
        fontWeight: '400',
        color: '#131313',
    },
};

const modifyNameOnCardField = async () => {
    const nameOnCardField = await waitForElement('.adyen-checkout__card__holderName__input');

    if (nameOnCardField) {
        nameOnCardField.setAttribute('maxlength', '30');
        nameOnCardField.removeAttribute('required');
    }
};

const brands = getCartBrandsForLocale(getLocale()).filter(item => item !== 'mastercard');

export const useAdyen = (): useAdyenInterface => {
    const [adyenForm, setAdyenForm] = useState<CardElement>();
    const [adyenFormState, setAdyenFormState] = useState<CardState>();

    const handleChange = useCallback((state: CardState) => {
        setAdyenFormState(state);
    }, []);

    useEffect(() => {
        const initializeAdyen = async () => {
            try {
                const { default: AdyenCheckout } = await import('@adyen/adyen-web');

                const configuration: CoreOptions = {
                    ...adyenBaseConfiguration,
                    onChange: (state: CardState) => {
                        handleChange(state);
                    },
                    paymentMethodsConfiguration: {
                        card: {
                            hasHolderName: true,
                            holderNameRequired: true,
                            styles: adyenCardStyles,
                        },
                    },
                    showPayButton: false,
                };

                const checkout = await AdyenCheckout(configuration);
                const component = checkout
                    .create('card', {
                        brands,
                    })
                    .mount('#adyen-card-container');

                setAdyenForm(component);
                await modifyNameOnCardField();
            } catch (error) {
                console.error('Error initializing Adyen:', error);
            }
        };

        initializeAdyen();
    }, [handleChange]);

    return {
        adyenForm,
        adyenFormState,
    };
};
